import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface MovieCardProps {
  movie: Movie;
}

export function MovieCard({ movie }: MovieCardProps) {
  return (
    <Link
      to={`/article/${movie.id}`}
      className="group block rounded-xl overflow-hidden bg-background-100 border border-background-200/40 hover:border-primary-500/20 transition-all duration-300 cursor-pointer"
      data-product-shop
    >
      {/* Poster */}
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={movie.poster}
          alt={movie.title}
          title={`${movie.title} - 大师兄影视免费在线观看`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
          <span className="text-white text-xs bg-primary-500/90 px-2.5 py-1 rounded-full whitespace-nowrap">
            点击查看
          </span>
        </div>
        {/* Rating badge */}
        <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-lg px-2 py-1 flex items-center gap-1">
          <i className="ri-star-fill text-accent-500 text-xs"></i>
          <span className="text-white text-xs font-medium">{movie.rating}</span>
        </div>
        {/* Update badge */}
        {(movie.isNew || movie.isHot) && (
          <div className="absolute top-2 left-2">
            {movie.isHot && (
              <span className="bg-primary-500 text-white text-xs px-2 py-0.5 rounded whitespace-nowrap">
                热播
              </span>
            )}
            {movie.isNew && !movie.isHot && (
              <span className="bg-accent-500 text-background-950 text-xs px-2 py-0.5 rounded whitespace-nowrap">
                新上线
              </span>
            )}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-3 md:p-4">
        <h3 className="text-sm md:text-base font-medium text-foreground-100 truncate group-hover:text-primary-300 transition-colors">
          {movie.title}
        </h3>
        <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
          <span>{movie.year}</span>
          <span className="w-0.5 h-0.5 rounded-full bg-foreground-600"></span>
          <span className="truncate">{movie.category}</span>
        </div>
        {movie.tags && movie.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {movie.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="text-xs px-2 py-0.5 rounded bg-background-200/60 text-foreground-500 whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}

interface MovieGridProps {
  title: string;
  movies: Movie[];
  icon?: string;
  viewAllLink?: string;
}

export function MovieGrid({ title, movies, icon, viewAllLink }: MovieGridProps) {
  return (
    <section className="w-full px-4 md:px-6 lg:px-10 py-8 md:py-12">
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <div className="flex items-center gap-3">
          {icon && (
            <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-xl bg-primary-500/10 text-primary-400">
              <i className={`${icon} text-lg md:text-xl`}></i>
            </div>
          )}
          <h2 className="text-lg md:text-2xl font-bold text-foreground-100 font-heading">
            {title}
          </h2>
        </div>
        {viewAllLink && (
          <Link
            to={viewAllLink}
            className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-300 transition-colors whitespace-nowrap cursor-pointer"
          >
            查看全部
            <i className="ri-arrow-right-line"></i>
          </Link>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
        {movies.map((movie) => (
          <MovieCard key={movie.id} movie={movie} />
        ))}
      </div>
    </section>
  );
}